// Primary button primitive. Plan 24.
//
// Discriminated union enforces that icon-only variants carry an
// `aria-label` at compile time — see plan 25 acceptance criteria. The
// `as` prop lets links participate in the same visual system without a
// second component.
//
// We model the `<button>` and `<a>` variants as two sibling prop shapes
// rather than an intersection, because `<button>` and `<a>` have
// overlapping-but-divergent HTML attribute surfaces (e.g. `disabled`,
// `href`) that TypeScript can't merge into a single interface.

import {
  forwardRef,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type ReactNode,
  type Ref,
} from 'react';
import { cn } from '../utils/cn';
import { Spinner } from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface BaseShared {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Replaces children with a spinner while retaining button size. */
  loading?: boolean;
  /** Leading icon (decorative; callers label the button itself). */
  icon?: ReactNode;
  className?: string;
  fullWidth?: boolean;
}

type ButtonHtmlProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  keyof BaseShared | 'aria-label' | 'children'
>;
type AnchorHtmlProps = Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  keyof BaseShared | 'aria-label' | 'children'
>;

type Labelled = { children: ReactNode; 'aria-label'?: string };
type IconOnly = { children?: never; icon: ReactNode; 'aria-label': string };

type ButtonElementProps = BaseShared & ButtonHtmlProps & { as?: 'button' };
type AnchorElementProps = BaseShared & AnchorHtmlProps & { as: 'a' };

export type ButtonProps =
  | (ButtonElementProps & Labelled)
  | (ButtonElementProps & IconOnly)
  | (AnchorElementProps & Labelled)
  | (AnchorElementProps & IconOnly);

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'px-4 py-2 text-xs',
  md: 'px-6 py-3 text-sm',
  lg: 'px-8 py-4 text-sm',
};

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary: 'bg-stone-900 text-stone-50 hover:bg-stone-800 shadow-lg disabled:opacity-50',
  secondary:
    'bg-stone-100 text-stone-900 hover:bg-stone-200 border border-stone-200 disabled:opacity-50',
  ghost:
    'bg-transparent text-stone-600 hover:bg-stone-100 hover:text-stone-900 disabled:opacity-50',
  danger: 'bg-red-600 text-white hover:bg-red-700 shadow-lg disabled:opacity-50',
};

const BASE =
  'inline-flex items-center justify-center gap-2 rounded-full font-medium transition-all active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2 focus-visible:ring-offset-stone-50 disabled:pointer-events-none';

type ButtonElement = HTMLButtonElement | HTMLAnchorElement;

export const Button = forwardRef<ButtonElement, ButtonProps>(function Button(props, ref) {
  const {
    variant = 'primary',
    size = 'md',
    loading = false,
    icon,
    className,
    fullWidth,
    children,
    // `as` must be extracted separately because the union makes the
    // destructured shape unknown to TS without a cast.
  } = props as BaseShared & { children?: ReactNode; icon?: ReactNode };

  const merged = cn(
    BASE,
    SIZE_CLASSES[size],
    VARIANT_CLASSES[variant],
    fullWidth && 'w-full',
    className
  );

  const content = (
    <>
      {loading ? (
        <Spinner size="sm" label="" className="text-current" />
      ) : (
        <>
          {icon && (
            <span className="inline-flex" aria-hidden="true">
              {icon}
            </span>
          )}
          {children}
        </>
      )}
    </>
  );

  if (props.as === 'a') {
    const {
      as: _a,
      variant: _v,
      size: _s,
      loading: _l,
      icon: _i,
      className: _c,
      fullWidth: _f,
      children: _ch,
      ...anchorRest
    } = props as AnchorElementProps & Labelled;
    void _a;
    void _v;
    void _s;
    void _l;
    void _i;
    void _c;
    void _f;
    void _ch;
    return (
      <a
        ref={ref as Ref<HTMLAnchorElement>}
        className={merged}
        {...(anchorRest as AnchorHtmlProps)}
      >
        {content}
      </a>
    );
  }

  const {
    as: _a,
    variant: _v,
    size: _s,
    loading: _l,
    icon: _i,
    className: _c,
    fullWidth: _f,
    children: _ch,
    disabled,
    type,
    ...buttonRest
  } = props as ButtonElementProps &
    Labelled & {
      disabled?: boolean;
      type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
    };
  void _a;
  void _v;
  void _s;
  void _l;
  void _i;
  void _c;
  void _f;
  void _ch;

  return (
    <button
      ref={ref as Ref<HTMLButtonElement>}
      type={type ?? 'button'}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={merged}
      {...(buttonRest as ButtonHtmlProps)}
    >
      {content}
    </button>
  );
});
